import React, { useState } from 'react';
import {
    AzureMap,
    AzureMapDataSourceProvider,
    AzureMapLayerProvider,
    AzureMapPopup,
    AzureMapsProvider,
} from 'react-azure-maps';
import 'azure-maps-control/dist/atlas.min.css';
import { useAppContext } from './AppContext';
import { IMerchant } from '../../../shared/interfaces/IReceipAnalyzerEntities';
import { IconButton } from '@fluentui/react';
import { useNavigate } from 'react-router';

const AZURE_MAPS_KEY = process.env.REACT_APP_AZURE_MAPS_KEY || '8ZXDMyINThw7zb39wZB7qpCYeYSNpsFkwCLOojX6mdtqzPJTTAbCJQQJ99BJAC5RqLJ1g9NWAAAgAZMP3X7c';

interface HeathMapProps {
    fullscreen?: boolean;
    onClose?: () => void;
}

export default function HeathMap({ fullscreen, onClose }: HeathMapProps) {
    const [popupLocation, setPopupLocation] = useState<number[] | null>(null);
    const [popupContent, setPopupContent] = useState('');
    const [popupCount, setPopupCount] = useState(0);
    const [userLocation, setUserLocation] = useState<[number, number] | null>(null);
    const [locationDenied, setLocationDenied] = useState(false);
    const [showSettings, setShowSettings] = useState(false);
    const [heatmapRadius, setHeatmapRadius] = useState(30);
    const [heatmapOpacity, setHeatmapOpacity] = useState(0.8);
    const [showBubbles, setShowBubbles] = useState(true);
    const [popupMerchantId, setPopupMerchantId] = useState<string | null>(null);
    const [popupCity, setPopupCity] = useState('');
    const { appData, setAppData } = useAppContext();
    const navigate = useNavigate();

    // Request location only when fullscreen opens (first time user interacts with map)
    React.useEffect(() => {
        if (!fullscreen) return;
        if (userLocation || locationDenied) return;
        if (!navigator.geolocation) return;
        navigator.geolocation.getCurrentPosition(
            (position) => {
                setUserLocation([position.coords.longitude, position.coords.latitude]);
            },
            () => {
                setLocationDenied(true);
            }
        );
    }, [fullscreen]);

    // Group receipts by merchant location and count unique visit days
    const locationFrequency = new Map<string, { coordinates: number[]; title: string; count: number; visitDates: Set<string>; merchantId: string; city: string; totalSpent: number }>();
    
    appData.data.reportReceipts.forEach((receipt) => {
        const merchant = appData.data.reportMerchants.find(m => m.merchant_id === receipt.merchant_id);
        if (merchant && merchant.longitude && merchant.latitude) {
            const key = `${merchant.longitude},${merchant.latitude}`;
            const existing = locationFrequency.get(key);
            const amount = Number(receipt.total_amount) || 0;
            
            // Extract date portion only (YYYY-MM-DD)
            const visitDate = receipt.transaction_date ? receipt.transaction_date.split('T')[0] : '';
            
            if (existing) {
                existing.visitDates.add(visitDate);
                existing.count = existing.visitDates.size;
                existing.totalSpent += amount;
            } else {
                const visitDates = new Set<string>();
                visitDates.add(visitDate);
                locationFrequency.set(key, {
                    coordinates: [merchant.longitude, merchant.latitude],
                    title: merchant.name || 'Unknown Merchant',
                    count: 1,
                    visitDates: visitDates,
                    merchantId: merchant.merchant_id,
                    city: merchant.city || '',
                    totalSpent: amount,
                });
            }
        }
    });

    // Find the location with the most visits to center the map
    let mapCenter: [number, number] = [4.3517, 50.8503]; // Default: Brussels
    
    // Use user's location if available, otherwise use most visited location
    if (userLocation) {
        mapCenter = userLocation;
    } else if (locationFrequency.size > 0) {
        const mostVisitedLocation = Array.from(locationFrequency.values()).reduce((prev, current) => 
            current.count > prev.count ? current : prev
        );
        mapCenter = [mostVisitedLocation.coordinates[0], mostVisitedLocation.coordinates[1]];
    }

    const mapOptions = {
        center: mapCenter,
        zoom: 10,
        authOptions: {
            authType: 'subscriptionKey',
            subscriptionKey: AZURE_MAPS_KEY,
        },
    };

    // Find max count for normalization
    const maxCount = Math.max(...Array.from(locationFrequency.values()).map(loc => loc.count), 1);

    const heatmapData = Array.from(locationFrequency.values()).map(loc => {
        // Normalize weight to 1-10 scale
        const normalizedWeight = Math.min(10, Math.max(1, Math.ceil((loc.count / maxCount) * 10)));
        
        return {
            type: 'Point' as const,
            coordinates: loc.coordinates,
            properties: {
                weight: normalizedWeight,
                title: loc.title,
                count: loc.count,
                merchantId: loc.merchantId,
                city: loc.city,
                totalSpent: Math.round(loc.totalSpent * 100) / 100,
            },
        };
    });

    const handleHeatmapClick = (e: any) => {
        if (e?.shapes && e.shapes.length > 0) {
            const shape = e.shapes[0];
            const coords = shape?.getCoordinates?.() || shape?.geometry?.coordinates;
            const props = shape?.getProperties?.() || shape?.properties || {};
            
            if (coords) {
                setPopupLocation(coords);
                setPopupContent(props.title || 'Unknown Location');
                setPopupCount(props.count || 1);
                setPopupMerchantId(props.merchantId || null);
                setPopupCity(props.city || '');
            }
        }
    };

    const handleDrilldown = () => {
        if (!popupMerchantId) return;
        if (fullscreen && onClose) onClose();
        navigate(`/Merchant?merchantId=${encodeURIComponent(popupMerchantId)}`);
    };

    const stopSwipe = (e: React.TouchEvent) => e.stopPropagation();

    const mapContent = (
        <AzureMapsProvider>
            <div className="heatmap-container" style={fullscreen ? { width: '100%', height: '100%', borderRadius: 0, margin: 0 } : undefined} onTouchStart={stopSwipe} onTouchEnd={stopSwipe}>
                <AzureMap options={mapOptions as any}>
                    <AzureMapDataSourceProvider
                        id="heatmapDataSource"
                        options={{}}
                        collection={{
                            type: 'FeatureCollection',
                            features: heatmapData.map(point => ({
                                type: 'Feature',
                                geometry: {
                                    type: point.type,
                                    coordinates: point.coordinates,
                                },
                                properties: point.properties,
                            })),
                        } as any}
                    >
                        {/* Heatmap Layer */}
                        <AzureMapLayerProvider
                            id="heatmapLayer"
                            type="HeatLayer"
                            options={{
                                radius: heatmapRadius,
                                opacity: heatmapOpacity,
                                weight: ['get', 'weight'],
                                color: [
                                    'interpolate',
                                    ['linear'],
                                    ['heatmap-density'],
                                    0, 'rgba(0,0,255,0)',
                                    0.1, 'royalblue',
                                    0.3, 'cyan',
                                    0.5, 'lime',
                                    0.7, 'yellow',
                                    1, 'red'
                                ],
                            } as any}
                        />
                        
                        {/* Bubble Layer for clickable points */}
                        {showBubbles ? (
                            <AzureMapLayerProvider
                                id="bubbleLayer"
                                type="BubbleLayer"
                                options={{
                                    radius: [
                                        'interpolate', ['linear'], ['get', 'weight'],
                                        1, 8,
                                        5, 14,
                                        10, 22,
                                    ],
                                    color: '#ffcc00',
                                    opacity: 0.85,
                                    strokeColor: '#111',
                                    strokeWidth: 2,
                                    strokeOpacity: 0.9,
                                } as any}
                                events={{
                                    click: handleHeatmapClick,
                                } as any}
                            />
                        ) : null}
                        {/* Symbol layer for visit count labels */}
                        {showBubbles ? (
                            <AzureMapLayerProvider
                                id="symbolLayer"
                                type="SymbolLayer"
                                options={{
                                    iconOptions: { image: 'none' },
                                    textOptions: {
                                        textField: ['to-string', ['get', 'count']],
                                        size: 11,
                                        font: ['StandardFont-Bold'],
                                        color: '#111',
                                        offset: [0, 0],
                                        allowOverlap: true,
                                    },
                                } as any}
                            />
                        ) : null}
                    </AzureMapDataSourceProvider>

                    {(popupLocation && (
                        <AzureMapPopup
                            isVisible={true}
                            options={{ position: popupLocation, closeButton: true } as any}
                            popupContent={
                                <div style={{ padding: '12px 14px', minWidth: 160, fontFamily: 'inherit' }}>
                                    <div style={{ fontWeight: 700, fontSize: '0.95em', marginBottom: 4 }}>{popupContent}</div>
                                    {popupCity && <div style={{ fontSize: '0.78em', color: '#888', marginBottom: 6 }}>{popupCity}</div>}
                                    <div style={{ display: 'flex', gap: 16, fontSize: '0.82em', color: '#555', marginBottom: popupMerchantId ? 10 : 0 }}>
                                        <span>🗓 {popupCount} visit{popupCount !== 1 ? 's' : ''}</span>
                                    </div>
                                    {popupMerchantId && (
                                        <button
                                            onClick={handleDrilldown}
                                            style={{
                                                width: '100%', padding: '7px 0', border: 'none', borderRadius: 8,
                                                background: '#ffcc00', color: '#111', fontWeight: 700,
                                                fontSize: '0.82em', cursor: 'pointer', fontFamily: 'inherit',
                                            }}
                                        >View merchant →</button>
                                    )}
                                </div>
                            }
                            events={[{ eventName: 'close', callback: () => { setPopupLocation(null); setPopupMerchantId(null); } } as any]}
                        />
                    )) as any}
                </AzureMap>
            </div>
        </AzureMapsProvider>
    );

    if (fullscreen) {
        return (
            <div style={{
                position: 'fixed', inset: 0, zIndex: 10000,
                background: '#111', display: 'flex', flexDirection: 'column',
            }}>
                {/* Close button */}
                <div style={{ position: 'absolute', top: 8, left: 8, zIndex: 10001 }}>
                    <button
                        className="closeBtn"
                        title="Close map"
                        aria-label="Close map"
                        onClick={onClose}
                    >✕</button>
                </div>

                {/* Settings button */}
                <div style={{ position: 'absolute', top: 8, right: 8, zIndex: 10001 }}>
                    <IconButton
                        iconProps={{ iconName: 'Settings' }}
                        title="Map settings"
                        ariaLabel="Map settings"
                        onClick={() => setShowSettings(!showSettings)}
                        styles={{
                            root: { backgroundColor: '#ffcc00', color: '#000', borderRadius: '50%', width: 38, height: 38 },
                            rootHovered: { backgroundColor: '#e6b800', color: '#000' },
                            rootPressed: { backgroundColor: '#ccaa00', color: '#000' },
                            icon: { color: '#000' },
                        }}
                    />
                </div>

                {/* Settings panel */}
                {showSettings && (
                    <div style={{
                        position: 'absolute', top: 54, right: 8, zIndex: 10001,
                        background: 'rgba(20,20,20,0.95)', borderRadius: 14,
                        padding: '14px 16px', minWidth: 260,
                        boxShadow: '0 4px 24px rgba(0,0,0,0.5)',
                        color: '#fff', textAlign: 'left',
                    }}>
                        <div style={{ fontWeight: 700, fontSize: '0.85em', marginBottom: 10, opacity: 0.6, textTransform: 'uppercase', letterSpacing: 0.5 }}>Map Settings</div>

                        {/* Heatmap radius */}
                        <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '5px 0', fontSize: '0.85em' }}>
                            <span>Radius</span>
                            <input type="range" min={10} max={80} value={heatmapRadius} onChange={e => setHeatmapRadius(Number(e.target.value))} style={{ width: 100, accentColor: '#ffcc00' }} />
                        </label>

                        {/* Heatmap opacity */}
                        <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '5px 0', fontSize: '0.85em' }}>
                            <span>Opacity</span>
                            <input type="range" min={10} max={100} value={Math.round(heatmapOpacity * 100)} onChange={e => setHeatmapOpacity(Number(e.target.value) / 100)} style={{ width: 100, accentColor: '#ffcc00' }} />
                        </label>

                        {/* Show bubbles */}
                        <label style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, padding: '5px 0', cursor: 'pointer', fontSize: '0.85em' }}>
                            <span>Show markers</span>
                            <input type="checkbox" checked={showBubbles} onChange={e => setShowBubbles(e.target.checked)} style={{ accentColor: '#ffcc00', width: 18, height: 18 }} />
                        </label>

                        {/* Location status */}
                        <div style={{ marginTop: 8, paddingTop: 8, borderTop: '1px solid rgba(255,255,255,0.1)', fontSize: '0.78em', opacity: 0.5 }}>
                            {userLocation ? '📍 Using your location' : locationDenied ? '📍 Location denied — using data center' : '📍 Detecting location…'}
                        </div>
                    </div>
                )}

                <div style={{ flex: 1 }}>
                    {mapContent}
                </div>
            </div>
        );
    }

    return mapContent;
}